import { useEffect, useState } from "react";
import { SpiralAnimation } from "./spiral-animation";
import logoSTUDYGRI from "../../assets/logo_studygri.png";
import { useLang } from "../../i18n/LangContext";

/**
 * IntroScreen - premium full-screen spiral intro
 * - Auto fades out after AUTO_DISMISS_MS
 * - Click "Enter" to skip immediately (faster fade)
 * - Shown once per browser session (sessionStorage flag)
 */

const INTRO_I18N = {
  uk: { title: "Ваш експерт з освіти за кордоном", tagline: "Your path · to the world" },
  ru: { title: "Ваш эксперт по образованию за границей", tagline: "Your path · to the world" },
  en: { title: "Your expert in studying abroad", tagline: "Your path · to the world" },
  pl: { title: "Twój ekspert od studiów za granicą", tagline: "Your path · to the world" },
  it: { title: "Il tuo esperto di studi all\u2019estero", tagline: "Your path · to the world" },
  es: { title: "Tu experto en estudios en el extranjero", tagline: "Your path · to the world" },
  ar: { title: "خبيرك في الدراسة بالخارج", tagline: "Your path · to the world" },
  zh: { title: "您的海外教育专家", tagline: "Your path · to the world" },
};

const AUTO_DISMISS_MS = 3500; // 3.5s spiral on screen
const FADE_OUT_MS = 1000; // smooth crossfade to homepage

const BRAND_COLORS = {
  bg: "#0B1430", // deeper navy
  particle: "#C9A96E", // gold
  trail: "#E8D5AB", // light gold trail
};

const IntroScreen = ({ onDone }) => {
  const { lang } = useLang();
  const intro = INTRO_I18N[lang] || INTRO_I18N.en;
  const [enterVisible, setEnterVisible] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);

  // Reveal "Enter" button after 1.4s so user sees the spiral first
  useEffect(() => {
    const t = setTimeout(() => setEnterVisible(true), 1400);
    return () => clearTimeout(t);
  }, []);

  // Auto-dismiss after AUTO_DISMISS_MS
  useEffect(() => {
    const t = setTimeout(() => triggerExit(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Allow keyboard skip (Enter / Esc / Space)
  useEffect(() => {
    const onKey = (e) => {
      if (["Enter", "Escape", " "].includes(e.key)) triggerExit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerExit = () => {
    if (fadingOut) return;
    // Unlock background audio playback (user gesture requirement)
    try { window.dispatchEvent(new Event("sg-audio-start")); } catch {}
    setFadingOut(true);
    setTimeout(() => onDone?.(), FADE_OUT_MS);
  };

  return (
    <div
      className="fixed inset-0 z-[9998] overflow-hidden"
      style={{
        background: "#0B1430",
        opacity: fadingOut ? 0 : 1,
        transition: `opacity ${FADE_OUT_MS}ms ease-out`,
        pointerEvents: fadingOut ? "none" : "auto",
      }}
      data-testid="intro-screen"
    >
      {/* Spiral animation */}
      <div className="absolute inset-0">
        <SpiralAnimation colors={BRAND_COLORS} />
      </div>

      {/* Brand logo at top */}
      <div
        className="absolute top-8 left-1/2 -translate-x-1/2 z-10"
        style={{
          opacity: enterVisible ? 1 : 0,
          transform: enterVisible ? "translate(-50%, 0)" : "translate(-50%, -8px)",
          transition: "opacity 1.2s ease-out, transform 1.2s ease-out",
        }}
      >
        <img
          src={logoSTUDYGRI}
          alt="STUDYGRI"
          className="h-10 md:h-12 w-auto opacity-90"
          draggable={false}
          style={{ filter: "brightness(0) invert(1)" }}
        />
      </div>

      {/* Enter button — centered */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
        style={{
          opacity: enterVisible ? 1 : 0,
          transform: enterVisible
            ? "translate(-50%, -50%)"
            : "translate(-50%, calc(-50% + 12px))",
          transition: "opacity 1.5s ease-out, transform 1.5s ease-out",
        }}
      >
        <button
          onClick={triggerExit}
          data-testid="intro-enter-btn"
          className="group relative inline-flex flex-col items-center gap-4 px-8 py-4 max-w-[90vw]"
          style={{ background: "transparent", border: "none", cursor: "pointer" }}
        >
          <span
            className="text-[11px] uppercase tracking-[0.4em] font-semibold"
            style={{ color: "#C9A96E" }}
          >
            STUDYGRI
          </span>
          <span
            className="text-2xl sm:text-3xl md:text-5xl font-light leading-[1.15] text-center transition-all duration-700"
            style={{
              color: "#FFFFFF",
              fontFamily: "'Forum', 'Cormorant Garamond', serif",
              letterSpacing: "0.01em",
              maxWidth: "16ch"
            }}
          >
            {intro.title}
          </span>
          <span
            className="block w-12 h-px mt-1 transition-all duration-700 group-hover:w-24"
            style={{ background: "#C9A96E" }}
          />
        </button>
      </div>

      {/* Tagline at bottom */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-center px-6"
        style={{
          opacity: enterVisible ? 0.7 : 0,
          transition: "opacity 1.8s ease-out 0.3s",
        }}
      >
        <p
          className="text-xs md:text-sm uppercase tracking-[0.32em] font-light"
          style={{ color: "rgba(232,213,171,0.75)" }}
        >
          Your path · to the world
        </p>
      </div>
    </div>
  );
};

export default IntroScreen;
